import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { OrgSelect } from "@/components/common/OrgSelect";
import { FacilityDialog } from "@/components/common/FacilityDialog";
import { Button } from "@/components/ui/button";
import { Plus, Hospital, Pencil, Power, MapPin, BedDouble, Loader2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { toast } from "sonner";

export default function PlatformFacilities() {
  const [orgId, setOrgId] = useState(null);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = (oid) => { if (!oid) return; setRows(null); api.get(`/facilities?organisation_id=${oid}`).then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(() => { load(orgId); }, [orgId]);

  const save = async (form) => {
    try {
      if (editing) await api.put(`/facilities/${editing.id}`, { ...form, organisation_id: orgId });
      else await api.post("/facilities", { ...form, organisation_id: orgId });
      toast.success(editing ? "Facility updated" : "Facility created");
      setOpen(false); load(orgId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (f) => { try { await api.patch(`/facilities/${f.id}/status`); toast.success("Status updated"); load(orgId); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div data-testid="platform-facilities-page">
      <PageHeader title="Facilities" subtitle="Hospitals, clinics and centres operated under each organisation."
        actions={<div className="flex items-center gap-2"><OrgSelect value={orgId} onChange={setOrgId} />
          <Button onClick={() => { setEditing(null); setOpen(true); }} disabled={!orgId} className="bg-blue-700 hover:bg-blue-800" data-testid="create-facility-button"><Plus className="h-4 w-4 mr-1.5" /> New Facility</Button></div>} />

      {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((f) => (
            <div key={f.id} className="bg-white border border-slate-200 rounded-md p-5 hover:border-slate-300 transition-colors" data-testid={`facility-card-${f.id}`}>
              <div className="flex items-start justify-between">
                <span className="h-10 w-10 rounded-md bg-blue-50 text-blue-700 grid place-items-center"><Hospital className="h-5 w-5" /></span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`facility-actions-${f.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => { setEditing(f); setOpen(true); }} data-testid={`facility-edit-${f.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => toggle(f)} data-testid={`facility-toggle-${f.id}`}><Power className="h-4 w-4 mr-2" /> {f.status === "active" ? "Deactivate" : "Activate"}</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="mt-3 font-display font-semibold text-slate-800 flex items-center gap-2">{f.name}{f.is_demo && <span className="text-[10px] font-mono uppercase text-amber-600 bg-amber-50 border border-amber-200 rounded px-1">demo</span>}</div>
              <div className="text-xs text-slate-400 font-mono mt-0.5">{f.code} · {f.facility_type}</div>
              <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-slate-400" /> {f.city}, {f.state}</div>
                <div className="flex items-center gap-2"><BedDouble className="h-3.5 w-3.5 text-slate-400" /> {f.beds} beds</div>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100"><StatusBadge status={f.status} /></div>
            </div>
          ))}
          {rows.length === 0 && <div className="col-span-full text-center text-slate-400 py-16 bg-white border border-slate-200 rounded-md">No facilities for this organisation yet.</div>}
        </div>
      )}

      <FacilityDialog open={open} onOpenChange={setOpen} initial={editing} onSave={save} title={editing ? "Edit Facility" : "New Facility"} />
    </div>
  );
}
